type CategoryFilterItemConfig = {
  childValues?: string[];
  childCheckedInit?: Record<string, boolean>;
  startExpanded?: boolean;
};

/**
 * Tri-state parent + children for the category filter tree.
 *
 * - Expansion is toggled ONLY via the chevron (toggleExpand). Checking does not expand.
 * - The parent checkbox is a summary of its children (like a file manager):
 *   all children checked → parent full; some → indeterminate (pomlčka); none → empty.
 * - Clicking the parent cascades its new state to every child.
 * - The real inputs are driven reactively via `:checked` bindings, so programmatic
 *   cascades never fire a child `change` event — one user action = exactly one submit.
 */
export const categoryFilterItem = (config: CategoryFilterItemConfig = {}) => ({
  expanded: config.startExpanded ?? false,
  childValues: config.childValues ?? [],
  children: { ...(config.childCheckedInit ?? {}) } as Record<string, boolean>,

  get childCount(): number {
    return this.childValues.length;
  },

  get checkedCount(): number {
    return this.childValues.filter((value) => this.children[value]).length;
  },

  // Parent is fully checked only when every child is checked → sends f_category (whole category).
  get parentChecked(): boolean {
    return this.childCount > 0 && this.checkedCount === this.childCount;
  },

  // Partial selection → pomlčka. Parent f_category is NOT sent, so only the
  // checked children (f_subcategory) narrow the result.
  get parentIndeterminate(): boolean {
    return this.checkedCount > 0 && this.checkedCount < this.childCount;
  },

  toggleExpand() {
    this.expanded = !this.expanded;
  },

  onParentToggle(checked: boolean) {
    this.childValues.forEach((value) => {
      this.children[value] = checked;
    });
    this.submit();
  },

  setChild(value: string, checked: boolean) {
    this.children[value] = checked;
    this.submit();
  },

  submit() {
    // Wait for the `:checked` bindings to flush to the DOM before serializing the form.
    this.$nextTick(() => {
      const form = (this.$root as HTMLElement).closest('form');
      form?.requestSubmit();
    });
  },
});
